import { ReferralTypeState } from './referrals-selectors';
import { FAILURE, STARTING, SUCCESS } from '../action/util/action-name-creators';
import {
  Action,
  StateStatus,
  updateResourceByParam,
  setStateDirtyByParam,
} from '../reducers/utils/state-manager';

const InitialState: ReferralTypeState = {
  referralsById: {},
  referralsStateByOrgId: {},
  referralsStateByJobId: {},
};

const removeProperty = <T extends string | number | symbol>(obj: Record<T, any>, prop: T) => {
  const { [prop]: ignored, ...postPopObj } = obj;
  return postPopObj;
};

const updateReferralData = (
  state: ReferralTypeState,
  referralId: number,
  update: Partial<Referral>,
) => {
  if (Object.values(update).length === 0) {
    return state;
  }

  const updatedReferral = {
    ...state.referralsById[referralId],
    ...update,
  };

  const updatedReferrals = {
    ...state,
    referralsById: {
      ...state.referralsById,
      [updatedReferral.referralId]: updatedReferral,
    },
  };

  const updatedReferralsState = updatedReferral.Job
    ? {
        referralsStateByOrgId: {
          ...state.referralsStateByOrgId,
          [updatedReferral.Job.orgId]: StateStatus.dirty,
        },
        referralsStateByJobId: {
          ...state.referralsStateByJobId,
          [updatedReferral.Job.job_id]: StateStatus.dirty,
        },
      }
    : {};

  return { ...updatedReferrals, ...updatedReferralsState };
};

const getReferralsById = (referrals: Referral[]) =>
  referrals.reduce((referralsById: { [referralId: number]: Referral }, referral: Referral) => {
    referralsById[referral.referralId] = referral;
    return referralsById;
  }, {});

export const referralsReducer = (state = InitialState, action: Action) => {
  switch (action.type) {
    case STARTING('LOAD_REFERRALS_BY_ORG_ID'): {
      const {
        payload: {
          args: { orgId },
        },
      } = action;
      return updateResourceByParam(state, 'referrals', 'orgId', orgId, true);
    }
    case SUCCESS('LOAD_REFERRALS_BY_ORG_ID'): {
      const {
        payload: {
          args: { orgId },
          data,
        },
      } = action;
      return updateResourceByParam(
        state,
        'referrals',
        'orgId',
        orgId,
        false,
        getReferralsById(data),
      );
    }
    case FAILURE('LOAD_REFERRALS_BY_ORG_ID'): {
      const {
        payload: {
          args: { orgId },
        },
      } = action;
      return updateResourceByParam(state, 'referrals', 'orgId', orgId, false);
    }
    case STARTING('LOAD_REFERRALS_BY_JOB_ID'): {
      const {
        payload: {
          args: { jobId },
        },
      } = action;
      return updateResourceByParam(state, 'referrals', 'jobId', jobId, true);
    }
    case SUCCESS('LOAD_REFERRALS_BY_JOB_ID'): {
      const {
        payload: {
          args: { jobId },
          data,
        },
      } = action;
      return updateResourceByParam(
        state,
        'referrals',
        'jobId',
        jobId,
        false,
        getReferralsById(data),
      );
    }
    case FAILURE('LOAD_REFERRALS_BY_JOB_ID'): {
      const {
        payload: {
          args: { jobId },
        },
      } = action;
      return updateResourceByParam(state, 'referrals', 'jobId', jobId, false);
    }
    case SUCCESS('CREATE_REFERRAL'): {
      const { orgId } = action.payload.args;
      return setStateDirtyByParam(state, 'referrals', 'orgId', orgId);
    }
    case SUCCESS('LINK_REFERRAL_TO_EMPLOYEE'): {
      const { orgId, referralId, referringEmployeeId } = action.payload.args;
      if (!orgId || !referralId || !referringEmployeeId) return state;

      return updateReferralData(state, referralId, { referringEmployeeId });
    }
    case SUCCESS('DELETE_REFERRAL'): {
      const { referralId } = action.payload.args;
      const referral = state.referralsById[referralId];
      if (!referral || !referral.Job) return state;

      return {
        referralsById: removeProperty(state.referralsById, referralId),
        referralsStateByOrgId: removeProperty(state.referralsStateByOrgId, referral.Job.orgId),
        referralsStateByJobId: removeProperty(state.referralsStateByJobId, referral.Job.job_id),
      };
    }
    case SUCCESS('DELETE_EMPLOYEE'): {
      const { orgId } = action.payload.args;
      return setStateDirtyByParam(state, 'referrals', 'orgId', orgId);
    }
    default:
      return state;
  }
};
